import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { v4 as uuid } from "uuid";

import { useToast } from "@/components/ui/use-toast";

import { TPlanResponse } from "../clients/use-client-plans.hooks";
import { TProduct } from "../products/products.type";
import { createPlatform, deletePlatform, getPlatforms, updatePlatform } from "./platform.api";
import { IUsePlataform } from "./platform.hooks.interface";
import { plataformFormSchema, TPlataformFormData } from "./platform.schema";
import { TPlatform } from "./platform.types";

export const usePlataform = (): IUsePlataform => {
  const { toast } = useToast();
  const [isPlataformFormOpen, setIsPlataformFormOpen] =
    React.useState<boolean>(false);

  const [productsSelected, setProductsSelected] = useState<TProduct[]>([]);
  const [selectedPlans, setSelectedPlans] = React.useState<TPlanResponse[]>([]);
  const [platforms, setPlatforms] = React.useState<TPlatform[]>([]);
  const [isLoadingPlatforms, setIsLoadingPlatforms] =
    React.useState<boolean>(false);

  const [editingPlatform, setEditingPlatform] = React.useState<TPlatform | null>(null);
  const [isEditMode, setIsEditMode] = React.useState<boolean>(false);


  const [primaryColor, setPrimaryColor] = useState("#FFC700");
  const [secondaryColor, setSecondaryColor] = useState("#141414");
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");

  const plataformForm = useForm<TPlataformFormData>({
    resolver: zodResolver(plataformFormSchema),
    defaultValues: {
      planos: [],
    },
  });

  const [tabs, setTabs] = React.useState<{ id: string; label: string }[]>([]);
  const [newTabName, setNewTabName] = React.useState("");
  const [activeTab, setActiveTab] = React.useState("tab1");
  const [primaryImagePreview, setPrimaryImagePreview] = React.useState<
    string | undefined
  >(undefined);
  const [primaryLogoPreview, setPrimaryLogoPreview] = React.useState<
    string | undefined
  >(undefined);
  const [secondaryLogoPreview, setSecondaryLogoPreview] = React.useState<
    string | undefined
  >(undefined);
  const [primaryImage, setPrimaryImage] = React.useState<string | undefined>(
    undefined
  );
  const [primaryLogo, setPrimaryLogo] = React.useState<string | undefined>(
    undefined
  );
  const [secondaryLogo, setSecondaryLogo] = React.useState<string | undefined>(
    undefined
  );
  const [showPreview, setShowPreview] = React.useState<boolean>(false);

  const openPlataformForm = React.useCallback(() => {
    setEditingPlatform(null);
    setIsEditMode(false);
    plataformForm.reset({
      planos: [],
      landing_page: {
        title: "",
        description: "",
        primary_button_text: "",
        image_banner_primary: undefined,
        primary_logo: undefined,
        secondary_logo: undefined,
        whatsapp_number: "",
        instagram_url: "",
        facebook_url: "",
        contact_email: "",
        primary_color: "",
        secondary_color: "",
        background_color: "",
        title_font_family: "",
        body_font_family: "",
      },
      markup_value: undefined,
      commission_value: undefined,
      conversion_rate: undefined,
      tax_rate: undefined,
      cancellation_fee: undefined,
      operating_expenses: undefined,
      customer_base: undefined,
    });
    setSelectedPlans([]);
    setPrimaryColor("#FFC700");
    setSecondaryColor("#141414");
    setBackgroundColor("#ffffff");
    setPrimaryImagePreview(undefined);
    setPrimaryLogoPreview(undefined);
    setSecondaryLogoPreview(undefined);
    setIsPlataformFormOpen(true);
  }, [plataformForm]);

  const handleAddTab = (): { id: string; label: string } => {
    if (!newTabName.trim()) return { id: "", label: "" };

    const newId = uuid();
    setTabs([...tabs, { id: newId, label: newTabName }]);
    setActiveTab(newId);

    setNewTabName("");
    return { id: newId, label: newTabName };
  };

  const addTabToForm = (form: UseFormReturn<TPlataformFormData>) => {
    if (!newTabName.trim()) return;
    const newTab = handleAddTab();
    if (!newTab.id) return;

    const currentPlanos = form.getValues("planos") || [];

    // Adiciona o ID do novo tab ao array de planos
    form.setValue("planos", [...currentPlanos, newTab.id], {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const deleteTabFromForm = (
    tabId: string,
    form: UseFormReturn<TPlataformFormData>
  ) => {
    const index = tabs.findIndex((t) => t.id === tabId);
    handleDeleteCombo(tabId);

    if (index !== -1) {
      const currentPlanos = form.getValues("planos") || [];
      const newPlanos = [...currentPlanos];
      newPlanos.splice(index, 1);
      form.setValue("planos", newPlanos, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  };

  const handleProductCheckboxChange = (
    checked: boolean,
    field: { value: string[]; onChange: (value: string[]) => void },
    productId: string
  ) => {
    if (checked) {
      field.onChange([...(field.value || []), productId]);
    } else {
      field.onChange(
        (field.value || []).filter((id: string) => id !== productId)
      );
    }
  };

  const handleUpdateTabLabel = (tabId: string, newLabel: string) => {
    setTabs(
      tabs.map((tab) => (tab.id === tabId ? { ...tab, label: newLabel } : tab))
    );
  };

  const handleDeleteCombo = (tabId: string) => {
    setTabs(tabs.filter((tab) => tab.id !== tabId));
  };

  const handlePrimaryImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPrimaryImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePrimaryLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPrimaryLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSecondaryLogoChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSecondaryLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePrimaryImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPrimaryImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSecondaryLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSecondaryLogo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePrimaryLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPrimaryLogo(file.name);
    }
  };

  // React.useEffect(() => {
  //   if (newTabName) {
  //     plataformForm.setValue("planos", [
  //       ...plataformForm.getValues("planos"),
  //       {
  //         id: `tab${tabs.length + 1}`,
  //         name: newTabName,
  //         description: "",
  //         products: [],
  //       },
  //     ]);
  //   }
  // }, [newTabName, plataformForm, tabs]);

  React.useEffect(() => {
    plataformForm.reset({
      planos: [],
      landing_page: {
        title: "",
        description: "",
        primary_button_text: "",
        image_banner_primary: undefined,
        primary_logo: undefined,
        secondary_logo: undefined,
        whatsapp_number: "",
        instagram_url: "",
        facebook_url: "",
        contact_email: "",
        primary_color: "",
        secondary_color: "",
        background_color: "",
        title_font_family: "",
        body_font_family: "",
      },
      markup_value: undefined,
      commission_value: undefined,
      conversion_rate: undefined,
      tax_rate: undefined,
      cancellation_fee: undefined,
      operating_expenses: undefined,
      customer_base: undefined,
    });
    // Apenas valores iniciais no mount; não depender de plataformForm para não sobrescrever edição
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePlanSelection = (plan: TPlanResponse, checked?: boolean) => {
    setSelectedPlans((prev) => {
      const exists = prev.some((p) => p.id === plan.id);

      if (typeof checked === "boolean") {
        if (checked && !exists) {
          if (prev.length >= 3) {
            toast({
              title: "Limite de planos atingido",
              description:
                "Você pode selecionar no máximo 3 planos para a landing page.",
              variant: "destructive",
            });
            return prev;
          }
          return [...prev, plan];
        }
        if (!checked && exists) {
          return prev.filter((p) => p.id !== plan.id);
        }
        return prev;
      }

      // Comportamento fallback (toggle)
      if (exists) return prev.filter((p) => p.id !== plan.id);
      if (prev.length >= 3) {
        toast({
          title: "Limite de planos atingido",
          description:
            "Você pode selecionar no máximo 3 planos para a landing page.",
          variant: "destructive",
        });
        return prev;
      }
      return [...prev, plan];
    });
  };

  const clearSelectedPlans = () => {
    setSelectedPlans([]);
  };

  const isPlanSelected = (planId: string) => {
    return selectedPlans.some((p) => p.id === planId);
  };

  // Função para buscar plataformas
  const fetchPlatforms = React.useCallback(
    async (organizationId: string, projectId?: string) => {
      if (!organizationId) return;

      setIsLoadingPlatforms(true);
      try {
        // getPlatforms agora aceita projectId como query parameter
        const platformsData = await getPlatforms(organizationId, projectId);
        setPlatforms(platformsData);
      } catch (error) {
        console.error("Erro ao buscar plataformas:", error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar as plataformas",
          variant: "destructive",
        });
      } finally {
        setIsLoadingPlatforms(false);
      }
    },
    [toast]
  );

  // Mutation para criar plataforma
  const createPlatformMutation = useMutation({
    mutationFn: async ({
      organizationId,
      formDataToSend,
    }: {
      organizationId: string;
      formDataToSend: FormData;
    }) => {
      return await createPlatform(organizationId, formDataToSend);
    },
    onSuccess: (_, variables) => {
      // Recarrega a lista de plataformas
      fetchPlatforms(variables.organizationId);
      
      // Fecha o formulário
      setIsPlataformFormOpen(false);

      // Mostra mensagem de sucesso
      toast({
        title: "Sucesso",
        description: "Plataforma criada com sucesso!",
      });
    },
    onError: (error) => {
      console.error("Erro ao criar plataforma:", error);
      toast({
        title: "Erro",
        description: "Não foi possível criar a plataforma",
        variant: "destructive",
      });
    },
  });

  // Função para preparar dados e chamar a mutation
  const createPlatformCallback = React.useCallback(
    async (organizationId: string, formData: TPlataformFormData) => {
      try {

        const formDataToSend = new FormData();

        // Adiciona os dados do formulário como JSON string
        const dataToSend = {
          ...(formData.project_id && { project_id: formData.project_id }), // Incluir project_id se fornecido
          plans: formData.planos || [],
          landing_page: {
            title: formData.landing_page.title,
            description: formData.landing_page.description,
            primary_button_text: formData.landing_page.primary_button_text,
            whatsapp_number: formData.landing_page.whatsapp_number,
            instagram_url: formData.landing_page.instagram_url,
            facebook_url: formData.landing_page.facebook_url,
            contact_email: formData.landing_page.contact_email,
            primary_color: formData.landing_page.primary_color,
            secondary_color: formData.landing_page.secondary_color,
            background_color: formData.landing_page.background_color,
            title_font_family: formData.landing_page.title_font_family,
            body_font_family: formData.landing_page.body_font_family,
          },
          markup_value: formData.markup_value,
          commission_value: formData.commission_value,
          conversion_rate: formData.conversion_rate,
          tax_rate: formData.tax_rate,
          cancellation_fee: formData.cancellation_fee,
          operating_expenses: formData.operating_expenses,
          customer_base: formData.customer_base,
        };

        formDataToSend.append("data", JSON.stringify(dataToSend));

        // Adicionar arquivos apenas se forem instâncias de File
        // Os arquivos devem ser enviados sem o prefixo landing_page.
        // pois o objeto landing_page já está dentro do JSON data
        if (formData.landing_page.image_banner_primary instanceof File) {
          formDataToSend.append(
            "image_banner_primary",
            formData.landing_page.image_banner_primary
          );
        }

        if (formData.landing_page.primary_logo instanceof File) {
          formDataToSend.append(
            "primary_logo",
            formData.landing_page.primary_logo
          );
        }

        if (formData.landing_page.secondary_logo instanceof File) {
          formDataToSend.append(
            "secondary_logo",
            formData.landing_page.secondary_logo
          );
        }

        return await createPlatformMutation.mutateAsync({
          organizationId,
          formDataToSend,
        });
      } catch (error) {
        console.error("Erro ao preparar dados da plataforma:", error);
        throw error;
      }
    },
    [createPlatformMutation]
  );

  const openEditPlatform = React.useCallback((platform: TPlatform) => {
    setEditingPlatform(platform);
    setIsEditMode(true);
    setIsPlataformFormOpen(true);
    
    const initialSelectedPlans = (platform.plans || []).map(plan => ({
      id: plan.id,
      name: plan.name,
      description: plan.description,
      plan_price: plan.plan_price,
      organization_id: "", 
      is_active: true,
      commission_lifetime_other_installments: 0,
      created_at: "",
      updated_at: "",
      products: [],
    }));
    setSelectedPlans(initialSelectedPlans);
    
    plataformForm.reset({
      planos: (platform.plans || []).map(plan => plan.id),
      landing_page: {
        title: platform.landing_page?.title || "",
        description: platform.landing_page?.description || "",
        primary_button_text: platform.landing_page?.primary_button_text || "",
        whatsapp_number: platform.landing_page?.whatsapp_number || "",
        instagram_url: platform.landing_page?.instagram_url || "",
        facebook_url: platform.landing_page?.facebook_url || "",
        contact_email: platform.landing_page?.contact_email || "",
        primary_color: platform.landing_page?.primary_color || "",
        secondary_color: platform.landing_page?.secondary_color || "",
        background_color: platform.landing_page?.background_color || "",
        title_font_family: platform.landing_page?.title_font_family || "",
        body_font_family: platform.landing_page?.body_font_family || "",
        image_banner_primary: undefined,
        primary_logo: undefined,
        secondary_logo: undefined,
      },
      markup_value: platform.markup_value,
      commission_value: platform.commission_value,
      conversion_rate: platform.conversion_rate,
      tax_rate: platform.tax_rate,
      cancellation_fee: platform.cancellation_fee,
      operating_expenses: platform.operating_expenses,
      customer_base: platform.customer_base,
    });

    if (platform.landing_page?.primary_color) {
      setPrimaryColor(platform.landing_page.primary_color);
    }
    if (platform.landing_page?.secondary_color) {
      setSecondaryColor(platform.landing_page.secondary_color);
    }
    if (platform.landing_page?.background_color) {
      setBackgroundColor(platform.landing_page.background_color);
    }
  }, [plataformForm]);
  
  const closeEditPlatform = React.useCallback(() => {
    setEditingPlatform(null);
    setIsEditMode(false);
    setIsPlataformFormOpen(false);
    
    plataformForm.reset({
      planos: [],
      landing_page: {
        title: "",
        description: "",
        primary_button_text: "",
        image_banner_primary: undefined,
        primary_logo: undefined,
        secondary_logo: undefined,
        whatsapp_number: "",
        instagram_url: "",
        facebook_url: "",
        contact_email: "",
        primary_color: "",
        secondary_color: "",
        background_color: "",
        title_font_family: "",
        body_font_family: "",
      },
      markup_value: undefined,
      commission_value: undefined,
      conversion_rate: undefined,
      tax_rate: undefined,
      cancellation_fee: undefined,
      operating_expenses: undefined,
      customer_base: undefined,
    });

    setSelectedPlans([]);
    setPrimaryColor("#FFC700");
    setSecondaryColor("#141414");
    setBackgroundColor("#ffffff");
    setPrimaryImagePreview(undefined);
    setPrimaryLogoPreview(undefined);
    setSecondaryLogoPreview(undefined);
  }, [plataformForm]);
  
  const updatePlatformMutation = useMutation({
    mutationFn: async ({
      organizationId,
      platformId,
      formDataToSend,
    }: {
      organizationId: string;
      platformId: string;
      formDataToSend: FormData;
    }) => {
      return await updatePlatform(organizationId, platformId, formDataToSend);
    },
    onSuccess: (_, variables) => {
      // Recarrega a lista de plataformas
      fetchPlatforms(variables.organizationId);
      
      closeEditPlatform();
      
      toast({
        title: "Sucesso",
        description: "Plataforma atualizada com sucesso!",
      });
    },
    onError: (error) => {
      console.error("Erro ao atualizar plataforma:", error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar a plataforma",
        variant: "destructive",
      });
    },
  });
  
  const updatePlatformCallback = React.useCallback(
    async (organizationId: string, platformId: string, formData: TPlataformFormData) => {
      try {
        const formDataToSend = new FormData();
        
        const dataToSend = {
          ...(formData.project_id && { project_id: formData.project_id }), // Incluir project_id se fornecido
          plans: formData.planos || [],
          landing_page: {
            title: formData.landing_page.title,
            description: formData.landing_page.description,
            primary_button_text: formData.landing_page.primary_button_text,
            whatsapp_number: formData.landing_page.whatsapp_number,
            instagram_url: formData.landing_page.instagram_url,
            facebook_url: formData.landing_page.facebook_url,
            contact_email: formData.landing_page.contact_email,
            primary_color: formData.landing_page.primary_color,
            secondary_color: formData.landing_page.secondary_color,
            background_color: formData.landing_page.background_color,
            title_font_family: formData.landing_page.title_font_family,
            body_font_family: formData.landing_page.body_font_family,
          },
          markup_value: formData.markup_value,
          commission_value: formData.commission_value,
          conversion_rate: formData.conversion_rate,
          tax_rate: formData.tax_rate,
          cancellation_fee: formData.cancellation_fee,
          operating_expenses: formData.operating_expenses,
          customer_base: formData.customer_base,
        };
        
        
        formDataToSend.append("data", JSON.stringify(dataToSend));
        
        // Os arquivos devem ser enviados sem o prefixo landing_page.
        // pois o objeto landing_page já está dentro do JSON data
        if (formData.landing_page.image_banner_primary instanceof File) {
          formDataToSend.append("image_banner_primary", formData.landing_page.image_banner_primary);
        }
        
        if (formData.landing_page.primary_logo instanceof File) {
          formDataToSend.append("primary_logo", formData.landing_page.primary_logo);
        }
        
        if (formData.landing_page.secondary_logo instanceof File) {
          formDataToSend.append("secondary_logo", formData.landing_page.secondary_logo);
        }
        
        
        return await updatePlatformMutation.mutateAsync({
          organizationId,
          platformId,
          formDataToSend,
        });
      } catch (error) {
        console.error("Erro ao preparar dados da plataforma:", error);
        throw error;
      }
    },
    [updatePlatformMutation]
  );

  const deletePlatformMutation = useMutation({
    mutationFn: async ({
      organizationId,
      platformId,
      body,
    }: {
      organizationId: string;
      platformId: string;
      body?: { confirm?: boolean; reason?: string };
    }) => {
      return await deletePlatform(organizationId, platformId, body);
    },
    onSuccess: (_, variables) => {
      setPlatforms((prev) => 
        prev.filter(platform => platform.id !== variables.platformId)
      );
      
      toast({
        title: "Sucesso",
        description: "Plataforma deletada com sucesso!",
      });
    },
    onError: (error) => {
      console.error("Erro ao deletar plataforma:", error);
      toast({
        title: "Erro",
        description: "Não foi possível deletar a plataforma",
        variant: "destructive",
      });
    },
  });

  const deletePlatformCallback = React.useCallback(
    async (organizationId: string, platformId: string, reason?: string) => {
      try {
        return await deletePlatformMutation.mutateAsync({
          organizationId,
          platformId,
          body: {
            confirm: true,
            reason: reason || "Plataforma removida pelo usuário",
          },
        });
      } catch (error) {
        console.error("Erro ao deletar plataforma:", error);
        throw error;
      }
    },
    [deletePlatformMutation]
  );

  return {
    isPlataformFormOpen,
    openPlataformForm,
    setIsPlataformFormOpen,
    plataformForm,
    primaryColor,
    secondaryColor,
    backgroundColor,
    setPrimaryColor,
    setSecondaryColor,
    setBackgroundColor,
    productsSelected,
    setProductsSelected,
    tabs,
    setTabs,
    newTabName,
    setNewTabName,
    activeTab,
    setActiveTab,
    primaryImagePreview,
    setPrimaryImagePreview,
    primaryLogoPreview,
    setPrimaryLogoPreview,
    secondaryLogoPreview,
    setSecondaryLogoPreview,
    primaryImage,
    setPrimaryImage,
    primaryLogo,
    setPrimaryLogo,
    secondaryLogo,
    setSecondaryLogo,
    handleAddTab,
    handleProductCheckboxChange,
    handleUpdateTabLabel,
    handleDeleteCombo,
    addTabToForm,
    deleteTabFromForm,
    handlePrimaryImageChange,
    handleSecondaryLogoChange,
    handlePrimaryImage,
    handlePrimaryLogo,
    handleSecondaryLogo,
    handlePrimaryLogoChange,
    showPreview,
    setShowPreview,
    selectedPlans,
    togglePlanSelection,
    clearSelectedPlans,
    isPlanSelected,
    platforms,
    isLoadingPlatforms,
    fetchPlatforms,
    createPlatformCallback,
    isCreatingPlatform: createPlatformMutation.isPending,

    editingPlatform,
    isEditMode,
    openEditPlatform,
    closeEditPlatform,
    updatePlatformCallback,
    isUpdatingPlatform: updatePlatformMutation.isPending,
    deletePlatformCallback,
    isDeletingPlatform: deletePlatformMutation.isPending,
  };
};
